import { create } from 'zustand';
import { mainStore } from './main';

// ============================================================================
// Field lists
// ============================================================================

const ENRICHED_FIELDS = new Set([
    'layer', 'start', 'duration', 'actualDuration', 'displayDuration',
    'audioUrl', 'draggable', 'filename', 'totalDuration',
    'wavesurfer', 'waveform', 'audioBuffer', 'needsReload',
]);

const MUTABLE_FIELDS = ['timecode', 'instanceDuration', 'startPlayback', 'priority', 'title', 'label', 'status'];

/**
 * Strip enriched timeline fields from a cuesheet item, keeping only clean API fields.
 */
export function cleanItem(item) {
    const result = {};
    for (const key of Object.keys(item)) {
        if (!ENRICHED_FIELDS.has(key)) {
            result[key] = item[key];
        }
    }
    return result;
}

// ============================================================================
// Patch helpers
// ============================================================================

/**
 * Compute a patch describing the diff between beforeItems and afterItems.
 * Only diffs MUTABLE_FIELDS for "changed" entries.
 */
function computePatch(beforeItems, afterItems) {
    const beforeMap = new Map(beforeItems.map(i => [i.id, i]));
    const afterMap = new Map(afterItems.map(i => [i.id, i]));

    const changed = {};
    const removed = [];
    const added = [];

    // Detect changed and removed
    for (const [id, before] of beforeMap) {
        if (!afterMap.has(id)) {
            removed.push(before);
        } else {
            const after = afterMap.get(id);
            const beforeMutable = {};
            const afterMutable = {};
            let hasDiff = false;
            for (const field of MUTABLE_FIELDS) {
                if (before[field] !== after[field]) {
                    hasDiff = true;
                    beforeMutable[field] = before[field];
                    afterMutable[field] = after[field];
                }
            }
            if (hasDiff) {
                changed[id] = { before: beforeMutable, after: afterMutable };
            }
        }
    }

    // Detect added
    for (const [id, after] of afterMap) {
        if (!beforeMap.has(id)) {
            added.push(after);
        }
    }

    return { changed, removed, added };
}

function isPatchEmpty({ changed, removed, added }) {
    return (
        Object.keys(changed).length === 0 &&
        removed.length === 0 &&
        added.length === 0
    );
}

/**
 * Apply a patch forward (redo direction) to a clean items array.
 */
function applyPatchForward(items, patch) {
    const { changed, removed, added } = patch;
    const removedIds = new Set(removed.map(i => i.id));
    const addedIds = new Set(added.map(i => i.id));

    return [
        ...items
            .filter(item => !addedIds.has(item.id))  // remove items that were "added" (undo them)
            .map(item => {
                if (removedIds.has(item.id)) return item; // removed items stay (they were removed, re-adding on redo)
                if (changed[item.id]) {
                    return { ...item, ...changed[item.id].after };
                }
                return item;
            }),
        ...removed, // re-add items that were removed by this action
    ];
}

/**
 * Apply a patch backward (undo direction) to a clean items array.
 */
function applyPatchBackward(items, patch) {
    const { changed, removed, added } = patch;
    const addedIds = new Set(added.map(i => i.id));

    return [
        ...items
            .filter(item => !addedIds.has(item.id))  // remove items that were "added" by this action
            .map(item => {
                if (changed[item.id]) {
                    return { ...item, ...changed[item.id].before };
                }
                return item;
            }),
        ...added.filter(i => { // re-add items that existed before (were removed by this action)
            // "removed" items need to come back on undo — but we handle that differently:
            // items in "removed" are those that disappeared when this action ran
            // so on undo we need to add them back
            return false; // handled separately below
        }),
    ].concat(removed); // bring back items that were removed by this action
}

// ============================================================================
// Factory
// ============================================================================

export function createHistoryStore({ getWatchedState, setWatchedState, maxHistory = 50 }) {
    let _pendingLabel = null;
    let _pendingProjectId = null;
    let _activeProjectId = null;
    let _beforeSnapshot = null;
    let _debounceTimer = null;

    const useHistoryStore = create((set, get) => ({
        undoStack: [],
        redoStack: [],
        canUndo: false,
        canRedo: false,
        undoLabel: null,
        redoLabel: null,

        beginAction(label) {
            if (_pendingLabel !== null) {
                if (_pendingLabel === label) return; // same action type — idempotent (prevents double-call)
                // Different action: commit the pending one synchronously, then start fresh
                if (_debounceTimer) { clearTimeout(_debounceTimer); _debounceTimer = null; }
                get()._commitPatch();
            }
            _pendingLabel = label;
            const state = getWatchedState();
            _beforeSnapshot = state.cueSheet.map(cleanItem);
        },

        onCueSheetUpdated(projectId) {
            if (_pendingLabel === null) return; // no action in progress
            if (projectId) {
                _pendingProjectId = projectId;
                _activeProjectId = projectId;
            }
            if (_debounceTimer) clearTimeout(_debounceTimer);
            _debounceTimer = setTimeout(() => {
                _debounceTimer = null;
                get()._commitPatch();
            }, 50);
        },

        _commitPatch() {
            if (_pendingLabel === null || _beforeSnapshot === null) return;

            const label = _pendingLabel;
            _pendingLabel = null;

            const afterState = getWatchedState();
            const afterItems = afterState.cueSheet.map(cleanItem);
            const patch = computePatch(_beforeSnapshot, afterItems);
            _beforeSnapshot = null;

            if (isPatchEmpty(patch)) {
                return;
            }

            set((state) => {
                const newUndoStack = [
                    ...state.undoStack.slice(-(maxHistory - 1)),
                    { label, patch },
                ];
                return {
                    undoStack: newUndoStack,
                    redoStack: [],
                    canUndo: true,
                    canRedo: false,
                    undoLabel: label,
                    redoLabel: null,
                };
            });
            const pid = _pendingProjectId;
            _pendingProjectId = null;
            mainStore.set('syncCueSheetWithHistory', pid, label, patch);
        },

        undo() {
            const { undoStack, redoStack } = get();
            if (undoStack.length === 0) return;

            const entry = undoStack[undoStack.length - 1];
            const currentItems = getWatchedState().cueSheet.map(cleanItem);
            const restoredItems = applyPatchBackward(currentItems, entry.patch);

            setWatchedState({ cueSheet: restoredItems });

            const newUndoStack = undoStack.slice(0, -1);
            const newRedoStack = [...redoStack, entry];

            set({
                undoStack: newUndoStack,
                redoStack: newRedoStack,
                canUndo: newUndoStack.length > 0,
                canRedo: true,
                undoLabel: newUndoStack.length > 0 ? newUndoStack[newUndoStack.length - 1].label : null,
                redoLabel: entry.label,
            });

            mainStore.set('saveHistoryItems', _activeProjectId, restoredItems);
        },

        redo() {
            const { undoStack, redoStack } = get();
            if (redoStack.length === 0) return;

            const entry = redoStack[redoStack.length - 1];
            const currentItems = getWatchedState().cueSheet.map(cleanItem);
            const restoredItems = applyPatchForward(currentItems, entry.patch);

            setWatchedState({ cueSheet: restoredItems });

            const newRedoStack = redoStack.slice(0, -1);
            const newUndoStack = [...undoStack, entry];

            set({
                undoStack: newUndoStack,
                redoStack: newRedoStack,
                canUndo: true,
                canRedo: newRedoStack.length > 0,
                undoLabel: entry.label,
                redoLabel: newRedoStack.length > 0 ? newRedoStack[newRedoStack.length - 1].label : null,
            });

            mainStore.set('saveHistoryItems', _activeProjectId, restoredItems);
        },

        clear() {
            if (_debounceTimer) {
                clearTimeout(_debounceTimer);
                _debounceTimer = null;
            }
            _pendingLabel = null;
            _beforeSnapshot = null;
            set({
                undoStack: [],
                redoStack: [],
                canUndo: false,
                canRedo: false,
                undoLabel: null,
                redoLabel: null,
            });
        },

        load(rawData) {
            if (_debounceTimer) {
                clearTimeout(_debounceTimer);
                _debounceTimer = null;
            }
            _pendingLabel = null;
            _beforeSnapshot = null;

            let undoStack = [];
            if (Array.isArray(rawData)) {
                undoStack = rawData;
            } else if (rawData?.body) {
                try {
                    const parsed = typeof rawData.body === 'string' ? JSON.parse(rawData.body) : rawData.body;
                    if (Array.isArray(parsed)) undoStack = parsed;
                } catch (_) { /* malformed — start fresh */ }
            }

            const capped = undoStack.slice(-maxHistory);
            set({
                undoStack: capped,
                redoStack: [],
                canUndo: capped.length > 0,
                canRedo: false,
                undoLabel: capped.length > 0 ? capped[capped.length - 1].label : null,
                redoLabel: null,
            });
        },
    }));

    return { useHistoryStore, historyStore: useHistoryStore };
}

// ============================================================================
// Project-specific instance
// ============================================================================

export const { useHistoryStore, historyStore } = createHistoryStore({
    getWatchedState: () => ({ cueSheet: mainStore.get('cueSheet') }),
    setWatchedState: ({ cueSheet }) => mainStore.set('cueSheet', cueSheet),
    maxHistory: 30,
});

// ============================================================================
// Hook
// ============================================================================

export function useCueSheetHistory() {
    const { canUndo, canRedo, undoLabel, redoLabel } = useHistoryStore(s => s);
    return {
        undo: () => historyStore.getState().undo(),
        redo: () => historyStore.getState().redo(),
        clear: () => historyStore.getState().clear(),
        canUndo,
        canRedo,
        undoLabel,
        redoLabel,
    };
}
